<script lang="ts" setup>
import OmniDialog from "@/components/OmniDialog.vue";
import DatePicker from "@/components/pickers/DatePicker.vue";
import TimePicker from "@/components/pickers/TimePicker.vue";
import type { RouteStopDto, TripRouteDto } from "@/api/trips/dto/trip.dto";
import { OrderStopType } from "@/data/order";
import { computed, ref, useTemplateRef, watch } from "vue";
import type { VForm } from "vuetify/components";
import { API } from "@/api/api";
import { useSnackbarStore } from "@/store/snackbar.store";
import { useDateTime } from "@/composables";
import validationMixin from "@/mixins/validation.mixin";
import dayjs from "dayjs";
import { useError } from "@/composables/useError";
import TimezoneSelect from "@/components/inputs/TimezoneSelect.vue";

const showDialog = defineModel<boolean>({ default: false });

const api = new API();

const { showSnackbarError } = useSnackbarStore();
const { timezonesShortList, dateToUTC } = useDateTime();
const { requiredValidator } = validationMixin.methods!;
const { showAPIError } = useError();

const formRef = useTemplateRef<VForm | null>("form");
const loading = ref<boolean>(false);
const checkInData = ref<{
  date: string;
  time: string;
  timezone: string;
}>({ date: "", time: "", timezone: "" });

const props = defineProps<{
  route: TripRouteDto;
  stop: RouteStopDto;
  tripId: number;
}>();

const emits = defineEmits<{
  (e: "update:modelValue", value: boolean): void;
  (e: "checkedIn"): void;
}>();

const stopType = computed(() => {
  switch (props.stop.type) {
    case OrderStopType.pickup:
      return "Pick up";
    case OrderStopType.delivery:
      return "Delivery";
    default:
      return "";
  }
});

async function checkIn() {
  if (!formRef.value) {
    return;
  }

  const { valid } = await formRef.value.validate();
  if (!valid) return;

  loading.value = true;

  try {
    const response = await api.trips.checkInRouteStop(props.tripId, props.stop.id!, {
      checkInTime: dateToUTC(checkInData.value.date + " " + checkInData.value.time, checkInData.value.timezone),
    });

    if (response.success) {
      emits("checkedIn");
      emits("update:modelValue", false);
    } else {
      showAPIError(response, "Error checking in at stop");
    }
  } catch (error) {
    console.error("Check-in error:", error);
    showSnackbarError("Unexpected error during check-in");
  } finally {
    loading.value = false;
  }
}

watch(
  showDialog,
  async (value) => {
    if (value) {
      checkInData.value.timezone = props.stop.facility?.timezone || "";
      const date = dayjs().tz(checkInData.value.timezone);
      checkInData.value.date = date.format("YYYY-MM-DD");
      checkInData.value.time = date.format("HH:mm");
    }
  },
  {
    immediate: true,
  },
);
</script>

<template>
  <omni-dialog
    v-model="showDialog"
    :disabled="loading"
    :loading="loading"
    :title="'Check in on the ' + stopType"
    primary-action-label="Check in"
    @primary-action:click="checkIn"
  >
    <v-form ref="form">
      <div class="mb-4 text-grey-darken-1">
        You are about to check in
        <router-link
          v-if="route.truck?.id"
          :to="{ name: 'truck-view', params: { id: route.truck.id } }"
          class="text-primary text-decoration-none"
          data-qa="card-truck-number"
          target="_blank"
        >
          Truck #{{ route.truck?.number }}
        </router-link>
        at the {{ stopType }}:
      </div>
      <div class="rounded bg-grey-lighten-4 pa-3 mb-8">
        <router-link
          :to="{ name: 'facilities-edit', params: { id: stop.facility?.id } }"
          class="text-primary font-weight-500 text-decoration-none d-block"
          data-qa="card-facility-name"
          target="_blank"
        >
          {{ stop.facility?.name }}
        </router-link>
        <div class="mt-2 font-size-12 text-grey-darken-2" data-qa="card-facility-address">
          {{ stop.facility?.addressLine }}, {{ stop.facility?.address }}
        </div>
      </div>
      <v-row>
        <v-col class="px-2" cols="4">
          <timezone-select
            v-model="checkInData.timezone"
            :items="timezonesShortList"
            :rules="[requiredValidator]"
            required
          />
        </v-col>
        <v-col class="px-2" cols="5">
          <date-picker v-model="checkInData.date" label="Date" required></date-picker>
        </v-col>
        <v-col class="px-2" cols="3">
          <time-picker v-model="checkInData.time" label="Time" required></time-picker>
        </v-col>
      </v-row>
    </v-form>
  </omni-dialog>
</template>

<style lang="scss" scoped></style>
